/**
 * The three places the delivery check appears: the global popup, the PDP and the cart.
 *
 * One element serves all three. They differ only in what they check and whether they
 * may touch the cart, which is what `context` decides:
 *
 *   popup  — area only. No SKUs, never touches the cart.
 *   pdp    — the product being viewed. Never touches the cart: adding a fee while
 *            someone is still browsing would be a surprise.
 *   cart   — the cart's lines, and owns the fee.
 *
 * All three read and write the same pincode through sbr-delivery-core, so entering it
 * anywhere fills the others in. That is the whole point of the shared module: the
 * shopper should never be asked twice.
 */

import {
  EVENT,
  STANDARD,
  load,
  hasPincode,
  isValidPincode,
  lookupPincode,
  resolve,
  refresh
} from './sbr-delivery-core.js'

const SEEN_KEY = 'sbr_popup_dismissed'

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ESCAPES[c])

/* --------------------------------------------------------------- the widget */

class SbrDelivery extends HTMLElement {
  connectedCallback() {
    this.context = this.dataset.context || 'cart'
    this.api = (this.dataset.api || '').trim()
    this.feeVariantId = (this.dataset.feeVariantId || '').trim()
    this.sku = (this.dataset.sku || '').trim()

    this.input = this.querySelector('[data-sbr-input]')
    this.button = this.querySelector('[data-sbr-button]')
    this.result = this.querySelector('[data-sbr-result]')
    if (!this.input || !this.button || !this.result) return

    // Cart only. The opt-in is remembered with the pincode, so a shopper who ticked it
    // in the drawer finds it ticked on the cart page.
    this.optIn = this.querySelector('[data-sbr-opt-in]')
    this.optStandard = this.querySelector('[data-sbr-opt-standard]')
    this.optInRow = this.querySelector('[data-sbr-opt-in-row]')
    const stored = load()
    this.wantFee =
      stored && typeof stored.fee === 'boolean' ? stored.fee : this.dataset.feeDefault === 'true'

    if (!this.api) {
      this.hidden = true
      return
    }

    this.ac = new AbortController()
    const signal = this.ac.signal
    this.button.addEventListener('click', this.submit.bind(this), { signal })
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        this.submit(e)
      }
    }, { signal })
    this.input.addEventListener('input', () => this.clearResult(), { signal })

    if (this.optIn) {
      this.syncChoice()
      // One handler for both radios: whichever moved, the fast one's state is the
      // answer, so there is no need to know which was clicked.
      const onChoice = () => {
        this.wantFee = this.optIn.checked
        // Not quiet: the cart total is about to move, so the wait should be visible
        // rather than the number changing on its own a moment later.
        this.revalidate(false)
      }
      this.optIn.addEventListener('change', onChoice, { signal })
      if (this.optStandard) this.optStandard.addEventListener('change', onChoice, { signal })
    }

    // Another instance resolved a pincode — mirror it without re-asking.
    document.addEventListener(EVENT, (e) => this.onShared(e.detail), { signal })

    // The cart changed under us: the fee and the verdict must both stay honest. These
    // are the theme's own events (assets/events.js); cart:build is not among them, so
    // the re-render our own fee change triggers cannot come back round as a loop.
    if (this.context === 'cart') {
      ;['cart:updated', 'ajaxProduct:added'].forEach((evt) =>
        document.addEventListener(evt, () => this.revalidate(), { signal })
      )
    }

    // A different variant is a different SKU, and a different SKU can have a
    // different answer — Pack 1 may be in the dark store while Pack 3 is not.
    if (this.context === 'pdp' && this.dataset.sectionId && this.dataset.productId) {
      document.addEventListener(
        `variant:change:${this.dataset.sectionId}:${this.dataset.productId}`,
        (e) => {
          const variant = e.detail && e.detail.variant
          this.sku = variant && variant.sku ? String(variant.sku).trim() : ''
          this.revalidate(false)
        },
        { signal }
      )
    }

    this.restore()
  }

  disconnectedCallback() {
    if (this.ac) this.ac.abort()
  }

  restore() {
    const s = load()
    if (!s || !s.pincode) return
    this.input.value = s.pincode
    if (s.verdict) this.paint(s.verdict)
    // On the cart and PDP a stored verdict may be about a different product or an
    // older cart, so re-derive it rather than trusting what was cached.
    if (this.context !== 'popup') this.revalidate()
  }

  /**
   * A pincode arrived from one of the other widgets.
   *
   * The pincode is shared but the answer is not: the popup asks about the area, the
   * PDP about one SKU, the cart about all of them, so a verdict earned elsewhere can
   * be wrong here. Everything but the popup therefore re-derives its own.
   *
   * lastPin is what stops that becoming a loop. Every resolve records the pincode it
   * ran for, so a widget ignores the echo of its own work and of work it has already
   * reacted to, and the round of re-derivations settles after one pass.
   */
  onShared(verdict) {
    if (!verdict || !verdict.pincode) return

    // Two things can have moved: the pincode, or the fast-delivery choice. Ignoring the
    // second would leave the drawer's tick out of step with the cart page's.
    const feeMoved = typeof verdict.fee === 'boolean' && verdict.fee !== this.wantFee
    if (verdict.pincode === this.lastPin && !feeMoved) return
    if (feeMoved) {
      this.wantFee = verdict.fee
      this.syncChoice()
    }

    this.lastPin = verdict.pincode
    this.input.value = verdict.pincode

    if (this.context === 'popup') {
      this.paint(verdict)
      return
    }
    this.revalidate(false)
  }

  /** Put the radio pair in step with the remembered choice. */
  syncChoice() {
    if (this.optIn) this.optIn.checked = this.wantFee
    if (this.optStandard) this.optStandard.checked = !this.wantFee
  }

  clearResult() {
    this.result.dataset.state = ''
    this.result.innerHTML = ''
    // The offer belonged to the answer that was just wiped, so it goes with it.
    if (this.optInRow) this.optInRow.hidden = true
  }

  async submit(event) {
    if (event) event.preventDefault()
    const pincode = (this.input.value || '').trim()
    if (!isValidPincode(pincode)) {
      this.paint({ state: 'invalid' })
      this.input.focus()
      return
    }
    await this.run(pincode)
  }

  /** Re-derive the answer for the pincode we already have. Quiet by default: a cart
   *  update should not flash a spinner at someone who did not ask for one. */
  async revalidate(quiet = true) {
    const s = load()
    if (s && s.pincode) await this.run(s.pincode, quiet)
  }

  async run(pincode, quiet) {
    // Same reason as the guard in the core: clicks overlap, requests finish out of
    // order, and an older one landing last repaints the choice the shopper just moved
    // away from.
    const seq = (this.runSeq = (this.runSeq || 0) + 1)
    this.lastPin = pincode
    // A quiet re-check still has to look like one. Left alone, a cached answer sits
    // there asserting itself and then flips a second later, which reads as a bug —
    // dimming it says "this is being re-checked" without clearing the answer away.
    if (quiet) this.checking(true)
    else this.loading(true)
    try {
      const verdict = await resolve(pincode, {
        api: this.api,
        feeVariantId: this.feeVariantId,
        skus: this.context === 'pdp' && this.sku ? [this.sku] : undefined,
        skipCart: this.context !== 'cart',
        // Only the popup may answer without checking anything. An empty cart has no
        // SKUs either, and it must not earn a fee.
        requireSkus: this.context !== 'popup',
        wantFee: this.wantFee
      })
      if (seq === this.runSeq) this.paint(verdict)
    } catch (e) {
      if (seq === this.runSeq) this.paint({ state: 'error' })
    } finally {
      if (quiet) this.checking(false)
      else this.loading(false)
    }
  }

  checking(on) {
    this.result.classList.toggle('is-checking', on)
  }

  loading(on) {
    this.button.classList.toggle('is-loading', on)
    this.button.disabled = on
    this.button.setAttribute('aria-busy', on ? 'true' : 'false')
    // Dim rather than clear. Clearing removed the answer AND the delivery choice on
    // every re-check, so picking an option made the options themselves disappear for
    // a second — which read as the widget resetting itself.
    this.checking(on)
  }

  paint(verdict) {
    if (!verdict || !verdict.state) return
    const d = this.dataset
    const state = verdict.state

    let icon = 'tick'
    let text
    if (state === 'invalid') {
      icon = 'cross'
      text = d.copyInvalid
    } else if (state === 'error') {
      icon = 'cross'
      text = d.copyError
    } else if (state === STANDARD) {
      icon = 'dot'
      // Covered area but nothing in stock reads differently from "we don't go there".
      text = verdict.reason === 'out-of-stock' ? d.copyOutOfStock : d.copyStandard
    } else {
      text = state === 'next-day' ? d.copyNextDay : d.copySameDay
    }

    // The offer only exists when the area and the stock both allow it, so the box
    // appears with the promise and goes away with it.
    if (this.optInRow) {
      this.optInRow.hidden = !(state === 'same-day' || state === 'next-day')
      this.syncChoice()
    }

    this.result.dataset.state = state
    this.result.innerHTML =
      `<span class="sbr-fd__icon" data-icon="${icon}" aria-hidden="true"></span>` +
      `<span class="sbr-fd__message">${esc(text)}</span>`
  }
}

/* ---------------------------------------------------------------- the popup */

class SbrDeliveryPopup extends HTMLElement {
  connectedCallback() {
    this.widget = this.querySelector('sbr-delivery')
    this.closeBtns = this.querySelectorAll('[data-sbr-popup-close]')
    this.ac = new AbortController()
    const signal = this.ac.signal

    this.closeBtns.forEach((b) =>
      b.addEventListener('click', () => this.dismiss(), { signal })
    )
    // Clicking the backdrop dismisses; clicking the panel must not.
    this.addEventListener('click', (e) => {
      if (e.target === this) this.dismiss()
    }, { signal })
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') this.dismiss()
    }, { signal })

    // Once a pincode lands — from here or anywhere else — the popup has done its job.
    document.addEventListener(EVENT, (e) => {
      if (e.detail && e.detail.state && e.detail.state !== 'invalid' && e.detail.state !== 'error') {
        setTimeout(() => this.dismiss(), 1200)
      }
    }, { signal })

    if (this.shouldShow()) {
      // A beat after load: appearing mid-paint feels like a bug, not an invitation.
      setTimeout(() => this.open(), Number(this.dataset.delay || 1200))
    }
  }

  disconnectedCallback() {
    if (this.ac) this.ac.abort()
  }

  shouldShow() {
    if (hasPincode()) return false
    try {
      return sessionStorage.getItem(SEEN_KEY) !== '1'
    } catch (e) {
      return true
    }
  }

  open() {
    this.classList.add('is-open')
    document.documentElement.classList.add('sbr-popup-open')
    const input = this.querySelector('[data-sbr-input]')
    if (input) input.focus()
  }

  dismiss() {
    if (!this.classList.contains('is-open')) return
    this.classList.remove('is-open')
    document.documentElement.classList.remove('sbr-popup-open')
    // sessionStorage, not localStorage: dismissing should quieten it for this visit,
    // not for ever.
    try {
      sessionStorage.setItem(SEEN_KEY, '1')
    } catch (e) {
      /* private mode */
    }
  }
}

if (!customElements.get('sbr-delivery')) customElements.define('sbr-delivery', SbrDelivery)
if (!customElements.get('sbr-delivery-popup')) customElements.define('sbr-delivery-popup', SbrDeliveryPopup)

export { SbrDelivery, SbrDeliveryPopup, lookupPincode, refresh }
